import Box from "@mui/material/Box";
import { Container, Typography, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme, alpha } from "@mui/material/styles";
import React, { useEffect } from "react";
// components
import DivesiteForm from "../components/NewDivesite/DivesiteForm";

// Glassmorphic page container matching dashboard style
const GlassPageContainer = styled(Container)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),

  "&::before": {
    content: '""',
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 40%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.02)} 0%, 
      transparent 40%), 
      radial-gradient(circle at 50% 50%, 
      ${alpha(theme.palette.background.paper, 0.03)} 0%, 
      transparent 60%)`,
    pointerEvents: "none",
    zIndex: -1,
  },
}));

// Header section with refined glassmorphic styling matching dashboard
const GlassHeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  textAlign: "center",
  marginBottom: theme.spacing(4),
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.7)} 0%, 
    ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      ${alpha(theme.palette.secondary.main, 0.3)}, 
      transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 25% 30%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 75% 70%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Enhanced glassmorphic content paper
const GlassContentPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.9)} 0%, 
    ${alpha(theme.palette.background.paper, 0.8)} 50%,
    ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
  backdropFilter: "blur(25px) saturate(1.15)",
  WebkitBackdropFilter: "blur(25px) saturate(1.15)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 24,
  padding: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.1)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.3)}, 
      transparent)`,
    borderRadius: "24px 24px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 25%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 75%, 
      ${alpha(theme.palette.secondary.main, 0.02)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Refined typography with better spacing
const GlassTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "2.75rem",
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main} 0%, 
    ${theme.palette.secondary.main} 70%, 
    ${theme.palette.primary.main} 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
  letterSpacing: "-0.015em",
  lineHeight: 1.1,
  marginBottom: theme.spacing(1.5),

  [theme.breakpoints.down("md")]: {
    fontSize: "2.25rem",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: "1.875rem",
  },
}));

const GlassSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 400,
  color: alpha(theme.palette.text.primary, 0.75),
  lineHeight: 1.5,
  maxWidth: 580,
  margin: "0 auto",

  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    lineHeight: 1.6,
  },
}));

export default function Newsite() {
  console.log("Newsite component rendered");
  const theme = useTheme();

  useEffect(() => {
    console.log("Newsite component mounted");
    return () => {
      console.log("Newsite component unmounted");
    };
  }, []);

  return (
    <GlassPageContainer maxWidth="md">
      <GlassHeaderBox>
        <GlassTitle variant="h2" gutterBottom>
          Add a New Divesite
        </GlassTitle>
        <GlassSubtitle>
          Share your favorite underwater destination with the diving community.
          Fill out the form below to add detailed information about this dive
          site.
        </GlassSubtitle>
      </GlassHeaderBox>

      <GlassContentPaper elevation={0}>
        <Box>
          <DivesiteForm />
        </Box>
      </GlassContentPaper>
    </GlassPageContainer>
  );
}
